import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Pressable, ScrollView, Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { ScreenContainer } from "@/shared/components/ui/screen-container";
import { Stack, useLocalSearchParams } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useCSSVariable } from "uniwind";
import { BackButton } from "@/shared/components/ui/back-button";
import { BottomSheet } from "@/shared/components/ui/bottom-sheet";
import { Button } from "@/shared/components/ui/button";
import { FormTextarea } from "@/shared/components/ui/form-textarea";
import { Heading } from "@/shared/components/ui/heading";
import { useBack } from "@/shared/hooks/use-back";
import { useReportUser } from "@/features/profile/api/reports";

const reportSchema = z.object({
  reasons: z.array(z.string()).min(1, "Selecione ao menos um motivo"),
  details: z.string().max(2000).optional(),
});

type ReportFormData = z.infer<typeof reportSchema>;

const REPORT_REASONS = [
  "Assédio ou comportamento inadequado",
  "Discurso de ódio ou discriminação",
  "Perfil falso ou identidade enganosa",
  "Homem ou pessoa que não respeita o propósito do app",
  "Outro motivo",
];

export default function ReportUserScreen() {
  const { userId } = useLocalSearchParams<{ userId: string }>();
  const insets = useSafeAreaInsets();
  const pink = useCSSVariable("--color-pink") as string;
  const [showSuccess, setShowSuccess] = useState(false);
  const { mutate: reportUser, isPending } = useReportUser();
  const goBack = useBack();

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<ReportFormData>({
    resolver: zodResolver(reportSchema),
    defaultValues: { reasons: [], details: "" },
    mode: "onChange",
  });

  const onSubmit = (values: ReportFormData) => {
    reportUser(
      {
        reportedUserId: userId,
        reasons: values.reasons,
        details: values.details ?? "",
      },
      { onSuccess: () => setShowSuccess(true) }
    );
  };

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <ScreenContainer style={{ paddingTop: 0 }}>
        {/* Header */}
        <View
          className="flex-row items-center gap-2 px-4 pb-4"
          style={{ paddingTop: insets.top || 24 }}
        >
          <BackButton accessibilityLabel="Voltar" />
          <Heading variant="section">Denunciar usuária</Heading>
        </View>

        <ScrollView
          contentContainerStyle={{ flexGrow: 1 }}
          keyboardShouldPersistTaps="handled"
        >
          <View className="flex-1 px-6">
            {/* Reasons */}
            <Controller
              control={control}
              name="reasons"
              render={({ field }) => (
                <View className="border-border border-t">
                  {REPORT_REASONS.map((reason) => {
                    const isSelected = field.value.includes(reason);
                    const toggle = () =>
                      field.onChange(
                        isSelected
                          ? field.value.filter((r) => r !== reason)
                          : [...field.value, reason]
                      );
                    return (
                      <Pressable
                        key={reason}
                        onPress={toggle}
                        className="border-border flex-row items-center gap-4 border-b py-5"
                        accessibilityRole="checkbox"
                        accessibilityState={{ checked: isSelected }}
                      >
                        <View
                          className="h-6 w-6 items-center justify-center rounded border-2"
                          style={{
                            borderColor: isSelected ? "#a3e635" : "#555",
                            backgroundColor: isSelected
                              ? "#a3e635"
                              : "transparent",
                          }}
                        >
                          {isSelected && (
                            <Ionicons name="checkmark" size={16} color="#000" />
                          )}
                        </View>
                        <Text className="text-foreground text-base font-medium">
                          {reason}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              )}
            />

            {/* Details */}
            <FormTextarea
              control={control}
              name="details"
              label="Detalhes"
              placeholder="Explique o motivo da denúncia"
              className="mt-6"
            />
          </View>
        </ScrollView>

        {/* Bottom actions */}
        <View
          className="border-border flex-row gap-3 border-t px-6 pt-4"
          style={{ paddingBottom: 20 }}
        >
          <Button variant="outline" className="flex-1" onPress={goBack}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            className="flex-1"
            disabled={!isValid}
            loading={isPending}
            onPress={handleSubmit(onSubmit)}
          >
            Denunciar
          </Button>
        </View>
      </ScreenContainer>

      {/* Success sheet */}
      <BottomSheet
        visible={showSuccess}
        onClose={() => {
          setShowSuccess(false);
          goBack();
        }}
        dismissOnBackdropPress={false}
      >
        <View className="mb-6 items-center gap-3 px-6 pt-6">
          <View className="bg-pink/20 h-16 w-16 items-center justify-center rounded-full">
            <Ionicons name="checkmark-circle" size={40} color={pink} />
          </View>
          <Heading variant="section">Denúncia enviada</Heading>
          <Text className="text-muted-foreground text-center text-base">
            Recebemos sua denúncia e iremos analisá-la.
          </Text>
        </View>
        <View className="px-6 pb-4">
          <Button
            variant="destructive"
            onPress={() => {
              setShowSuccess(false);
              goBack();
            }}
          >
            OK
          </Button>
        </View>
      </BottomSheet>
    </>
  );
}
